import { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import { DEMO } from "@/demo/demo";
import { login as apiLogin, logout as apiLogout, getStoredUser, getToken, syncUser } from "@/lib/auth";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(getStoredUser);

  // The cached user is a snapshot from login time and is never otherwise
  // refreshed — re-sync it once against the server's own view of the session
  // on app load, so a permission field added after this session's login
  // (e.g. is_admin) becomes visible without forcing a logout/login.
  useEffect(() => {
    if (!getStoredUser()) return;
    let cancelled = false;
    syncUser().then((fresh) => {
      // Guard against a logout that completed while this fetch was in
      // flight — applying a stale user after sessionStorage was already
      // cleared would silently resurrect a logged-out session's data.
      if (fresh && !cancelled && getToken()) setUser(fresh);
    });
    return () => { cancelled = true; };
  }, []);

  // A silent token refresh (401 interceptor, or a future caller) can happen
  // at any point deep inside an unrelated API call, with no direct path back
  // to this component — auth.js's syncUser() dispatches this event whenever
  // it re-syncs the cached user, so this context's `user` state (and every
  // permission-gated element reading it) stays current without requiring a
  // full page reload.
  useEffect(() => {
    const onUserSynced = (e) => {
      if (e.detail && getToken()) setUser(e.detail);
    };
    window.addEventListener("traceqc:user-synced", onUserSynced);
    return () => window.removeEventListener("traceqc:user-synced", onUserSynced);
  }, []);

  const login = useCallback(async (username, password) => {
    const data = await apiLogin(username, password);
    if (!data.require_totp) setUser(data.user);
    return data;
  }, []);

  // Called by the TOTP form after totpLogin() succeeds — updates context state
  // so ProtectedRoute sees an authenticated user and doesn't redirect to /login.
  const completeTotpLogin = useCallback((userData) => {
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    if (DEMO) return; // the demo viewer is always "signed in"
    // apiLogout now clears localStorage synchronously before the async server
    // call, so setUser(null) races nothing — both happen immediately.
    apiLogout();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, login, completeTotpLogin, logout }),
    [user, login, completeTotpLogin, logout],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
